import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { 
  fetchSortOptionsByCountryId,
  createSortOption, 
  updateSortOption, 
  deleteSortOption 
} from "@/services/countryService";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle
} from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Edit, Trash2, Plus, MoveUp, MoveDown, Languages } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface SortOption {
  id: string;
  name: string;
  name_ar?: string;
  name_tr?: string;
  field_name: string;
  description?: string;
  is_default: boolean;
  is_required: boolean;
  display_order: number;
}

interface SortOptionsManagerProps {
  countryId: string;
}

const SortOptionsManager: React.FC<SortOptionsManagerProps> = ({ countryId }) => {
  const { toast } = useToast();
  const { translate, isTranslating } = useTranslation();
  const [sortOptions, setSortOptions] = useState<SortOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption | null>(null);
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formNameAr, setFormNameAr] = useState('');
  const [formNameTr, setFormNameTr] = useState('');
  const [formFieldName, setFormFieldName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formIsRequired, setFormIsRequired] = useState(false);
  const [formOrder, setFormOrder] = useState(0);
  
  useEffect(() => {
    if (countryId) {
      loadSortOptions();
    }
  }, [countryId]);
  
  const loadSortOptions = async () => {
    setLoading(true);
    try {
      const sortOptionsData = await fetchSortOptionsByCountryId(countryId);
      setSortOptions(sortOptionsData);
    } catch (error) {
      console.error("Error loading sort options:", error);
      toast({
        title: "Error",
        description: "Failed to load sort options",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddSortOption = async () => {
    if (!formName.trim() || !formFieldName.trim()) {
      toast({
        title: "Error",
        description: "Name and field name are required",
        variant: "destructive",
      });
      return;
    }
    
    try {
      await createSortOption(
        countryId,
        formName.trim(),
        formFieldName.trim(),
        formIsDefault,
        formIsRequired,
        formOrder,
        formNameAr.trim() || undefined,
        formNameTr.trim() || undefined
      );
      
      toast({
        title: "Success",
        description: "Sort option added successfully",
      });
      
      setShowAddDialog(false);
      resetForm();
      loadSortOptions();
    } catch (error) {
      console.error("Error adding sort option:", error);
      toast({
        title: "Error",
        description: "Failed to add sort option",
        variant: "destructive",
      });
    }
  };
  
  const handleEditSortOption = async () => {
    if (!selectedSortOption || !formName.trim() || !formFieldName.trim()) {
      toast({
        title: "Error",
        description: "Name and field name are required",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const result = await updateSortOption(
        selectedSortOption.id,
        countryId,
        {
          name: formName.trim(),
          field_name: formFieldName.trim(),
          description: formDescription.trim(),
          is_default: formIsDefault,
          is_required: formIsRequired,
          display_order: formOrder,
          name_ar: formNameAr.trim() || null,
          name_tr: formNameTr.trim() || null
        }
      );

      if (!result.success) {
        throw new Error(result.error);
      }
      
      toast({
        title: "Success",
        description: "Sort option updated successfully",
      });
      
      setShowEditDialog(false);
      resetForm();
      loadSortOptions();
    } catch (error) {
      console.error("Error updating sort option:", error);
      toast({
        title: "Error",
        description: "Failed to update sort option",
        variant: "destructive",
      });
    }
  };
  
  const handleDeleteSortOption = async () => {
    if (!selectedSortOption) {
      return;
    }
    
    try {
      await deleteSortOption(selectedSortOption.id, countryId);
      
      toast({
        title: "Success",
        description: "Sort option deleted successfully",
      });
      
      setShowDeleteDialog(false);
      loadSortOptions();
    } catch (error) {
      console.error("Error deleting sort option:", error);
      toast({
        title: "Error",
        description: "Failed to delete sort option",
        variant: "destructive",
      });
    }
  };
  
  const openEditDialog = (sortOption: SortOption) => {
    setSelectedSortOption(sortOption);
    setFormName(sortOption.name);
    setFormNameAr(sortOption.name_ar || '');
    setFormNameTr(sortOption.name_tr || '');
    setFormFieldName(sortOption.field_name);
    setFormDescription(sortOption.description || '');
    setFormIsDefault(sortOption.is_default);
    setFormIsRequired(sortOption.is_required);
    setFormOrder(sortOption.display_order);
    setShowEditDialog(true);
  };
  
  const openDeleteDialog = (sortOption: SortOption) => {
    setSelectedSortOption(sortOption);
    setShowDeleteDialog(true);
  };
  
  const resetForm = () => {
    setFormName('');
    setFormNameAr('');
    setFormNameTr('');
    setFormFieldName('');
    setFormDescription('');
    setFormIsDefault(false);
    setFormIsRequired(false);
    setFormOrder(sortOptions.length);
    setSelectedSortOption(null);
  };
  
  const openAddDialog = () => {
    resetForm();
    setFormOrder(sortOptions.length);
    setShowAddDialog(true);
  };

  const handleTranslateAll = async () => {
    if (!sortOptions.length) return;
    
    setLoading(true);
    try {
      for (const option of sortOptions) {
        if (!option.name_ar || !option.name_tr) {
          const nameAr = await translate(option.name, 'ar');
          const nameTr = await translate(option.name, 'tr');
          
          await updateSortOption(option.id, countryId, {
            name: option.name,
            field_name: option.field_name,
            description: option.description || '',
            is_default: option.is_default,
            is_required: option.is_required,
            display_order: option.display_order,
            name_ar: nameAr,
            name_tr: nameTr
          });
        }
      }
      
      toast({
        title: "Success",
        description: "All sort options translated successfully",
      });
      
      loadSortOptions();
    } catch (error) {
      console.error('Error translating sort options:', error);
      toast({
        title: "Error",
        description: "Failed to translate sort options",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(sortOptions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update display orders
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index
    }));

    setLoading(true);
    try {
      // Update all items with new display orders
      for (const item of updatedItems) {
        const result = await updateSortOption(
          item.id,
          countryId,
          {
            name: item.name,
            field_name: item.field_name,
            description: item.description || '',
            is_default: item.is_default,
            is_required: item.is_required,
            display_order: item.display_order
          }
        );
        if (!result.success) {
          throw new Error(result.error);
        }
      }

      setSortOptions(updatedItems);
      toast({
        title: "Success",
        description: "Sort options reordered successfully",
      });
    } catch (error) {
      console.error("Error reordering sort options:", error);
      toast({
        title: "Error",
        description: "Failed to reorder sort options",
        variant: "destructive",
      });
      loadSortOptions(); // Reload original order on error
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium"><span>Sort Options</span></h3>
        <div className="flex gap-2">
          <Button 
            onClick={handleTranslateAll} 
            variant="outline" 
            className="flex items-center space-x-1"
            disabled={isTranslating || loading || !sortOptions.length}
          >
            <Languages className="h-4 w-4" />
            <span>{isTranslating ? 'Translating...' : 'Translate All'}</span>
          </Button>
          <Button onClick={openAddDialog} className="flex items-center space-x-1">
            <Plus className="h-4 w-4" />
            <span>Add Sort Option</span>
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading sort options...</p>
        </div>
      ) : sortOptions.length === 0 ? (
        <div className="text-center py-8 border rounded-md">
          <p className="text-muted-foreground">No sort options found for this country.</p>
          <Button onClick={openAddDialog} variant="outline" className="mt-4">
            Add your first sort option
          </Button>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Database Name</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Required</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <Droppable droppableId="sort-options">
              {(provided, snapshot) => (
                <TableBody
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {sortOptions.map((sortOption, index) => (
                    <Draggable
                      key={sortOption.id}
                      draggableId={sortOption.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={snapshot.isDragging ? "bg-muted" : ""}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{sortOption.name}</TableCell>
                          <TableCell>{sortOption.field_name}</TableCell>
                          <TableCell>{sortOption.is_default ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{sortOption.is_required ? 'Yes' : 'No'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(sortOption)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteDialog(sortOption)}
                                className="text-destructive"
                                disabled={sortOption.is_required}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      )}
      




      {/* Add Sort Option Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span>Add Sort Option</span></DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Name (English)</Label>
              <Input
                id="name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter sort option name in English"
              />
            </div>
            <div>
              <Label htmlFor="name-ar">Name (Arabic)</Label>
              <Input
                id="name-ar"
                value={formNameAr}
                onChange={(e) => setFormNameAr(e.target.value)}
                placeholder="Enter sort option name in Arabic"
              />
            </div>
            <div>
              <Label htmlFor="name-tr">Name (Turkish)</Label>
              <Input
                id="name-tr"
                value={formNameTr}
                onChange={(e) => setFormNameTr(e.target.value)}
                placeholder="Enter sort option name in Turkish"
              />
            </div>
            <div>
              <Label htmlFor="fieldName">Field Name</Label>
              <Input
                id="fieldName"
                value={formFieldName}
                onChange={(e) => setFormFieldName(e.target.value)}
                placeholder="Enter field name (e.g., 'extPick', 'faceValue')"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Enter description (optional)"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isDefault"
                checked={formIsDefault}
                onCheckedChange={(checked) => setFormIsDefault(checked === true)}
              />
              <Label htmlFor="isDefault">Default Sort Option</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isRequired"
                checked={formIsRequired}
                onCheckedChange={(checked) => setFormIsRequired(checked === true)}
              />
              <Label htmlFor="isRequired">Required Sort Option</Label>
            </div>
            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formOrder}
                onChange={(e) => setFormOrder(parseInt(e.target.value) || 0)}
                placeholder="Enter display order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddSortOption}>Add Sort Option</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Sort Option Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span>Edit Sort Option</span></DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Name (English)</Label>
              <Input
                id="edit-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter sort option name in English"
              />
            </div>
            <div>
              <Label htmlFor="edit-name-ar">Name (Arabic)</Label>
              <Input
                id="edit-name-ar"
                value={formNameAr}
                onChange={(e) => setFormNameAr(e.target.value)}
                placeholder="Enter sort option name in Arabic"
              />
            </div>
            <div>
              <Label htmlFor="edit-name-tr">Name (Turkish)</Label>
              <Input
                id="edit-name-tr"
                value={formNameTr}
                onChange={(e) => setFormNameTr(e.target.value)}
                placeholder="Enter sort option name in Turkish"
              />
            </div>
            <div>
              <Label htmlFor="edit-fieldName">Field Name</Label>
              <Input
                id="edit-fieldName"
                value={formFieldName}
                onChange={(e) => setFormFieldName(e.target.value)}
                placeholder="Enter field name"
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Enter description (optional)"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-isDefault"
                checked={formIsDefault}
                onCheckedChange={(checked) => setFormIsDefault(checked === true)}
              />
              <Label htmlFor="edit-isDefault">Default Sort Option</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-isRequired"
                checked={formIsRequired}
                onCheckedChange={(checked) => setFormIsRequired(checked === true)}
                disabled={selectedSortOption?.is_required}
              />
              <Label htmlFor="edit-isRequired">Required Sort Option</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSortOption}>Update Sort Option</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Sort Option Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span>Delete Sort Option</span></AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the sort option "{selectedSortOption?.name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSortOption} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SortOptionsManager;
